import { useCallback, useEffect, useState } from "react";
import {
  adminListCustomModels, adminAddCustomModel, adminDeleteCustomModel,
  adminListProviders, adminAddProvider, adminDeleteProvider,
  formatApiError,
} from "@/lib/api";
import { toast } from "sonner";
import { Trash, Plus, Crown, Lightning } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminBadge from "../components/AdminBadge";
import AdminFormInput from "../components/AdminFormInput";
import AdminFormSelect from "../components/AdminFormSelect";
import AdminPrimaryButton from "../components/AdminPrimaryButton";

// ── Providers sub-section ────────────────────────────────────────────────────
function ProvidersSection({ providers, onReload }) {
  const [form, setForm] = useState({ name: "", label: "", base_url: "", kind: "text" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminAddProvider(form);
      toast.success(`Provider "${form.name}" added`);
      setForm({ name: "", label: "", base_url: "", kind: "text" });
      await onReload();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete provider "${p.name}"? (Models using it must be removed first.)`)) return;
    try {
      await adminDeleteProvider(p.name);
      toast.success(`Provider "${p.name}" deleted`);
      await onReload();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  return (
    <AdminCard data-testid="providers-section">
      <div className="px-6 pt-6 pb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Providers · {providers.length}</p>
        <h3 className="text-lg font-semibold text-slate-900 mt-1 mb-1">LLM providers</h3>
        <p className="text-sm text-slate-500">
          Built-in providers use native SDK paths. Custom providers are treated as{" "}
          <span className="text-slate-700 font-medium">OpenAI-compatible</span> — works with Groq, DeepSeek, Together, Mistral, OpenRouter, Ollama, etc.
        </p>
      </div>

      <div className="overflow-x-auto" data-testid="providers-list">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50">
              {["Name", "Label", "Base URL", "Kind", "Source", ""].map((h) => (
                <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {providers.map((p) => (
              <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`provider-row-${p.name}`}>
                <td className="px-6 py-3 font-mono text-xs text-slate-700">{p.name}</td>
                <td className="px-6 py-3 font-semibold text-slate-900">{p.label}</td>
                <td className="px-6 py-3 font-mono text-xs text-slate-600 break-all max-w-[260px]">
                  <a href={p.base_url} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600 hover:underline">
                    {p.base_url}
                  </a>
                </td>
                <td className="px-6 py-3 text-xs uppercase tracking-wider text-slate-500">{p.kind}</td>
                <td className="px-6 py-3">
                  <AdminBadge variant={p.built_in ? "builtin" : "custom"}>
                    {p.built_in ? "built-in" : "custom"}
                  </AdminBadge>
                </td>
                <td className="px-6 py-3 text-right">
                  {!p.built_in && (
                    <button
                      onClick={() => remove(p)}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      data-testid={`provider-delete-${p.name}`}
                      aria-label={`Delete provider ${p.name}`}
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-6 border-t border-slate-100">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-4">Register new provider</p>
        <form onSubmit={add} className="grid md:grid-cols-2 gap-4" data-testid="provider-form">
          <AdminFormInput
            label="Provider name (slug)"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. groq, deepseek, together"
            data-testid="provider-name"
          />
          <AdminFormInput
            label="Display label"
            type="text"
            required
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            placeholder="e.g. Groq Cloud"
            data-testid="provider-label"
          />
          <div className="md:col-span-2">
            <AdminFormInput
              label="Base URL *"
              type="url"
              required
              value={form.base_url}
              onChange={(e) => setForm({ ...form, base_url: e.target.value })}
              placeholder="https://api.groq.com/openai/v1"
              hint="Full OpenAI-compatible base URL (usually ends with /v1). No trailing slash needed."
              data-testid="provider-base-url"
            />
          </div>
          <AdminFormSelect
            label="Supported kinds"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            data-testid="provider-kind"
          >
            <option value="text">Text only</option>
            <option value="image">Image only</option>
            <option value="both">Text &amp; image</option>
          </AdminFormSelect>
          <div className="flex items-end">
            <AdminPrimaryButton
              type="submit"
              busy={busy}
              busyLabel="Adding…"
              icon={<Plus size={14} weight="bold" />}
              className="w-full justify-center"
              data-testid="provider-submit"
            >
              Add provider
            </AdminPrimaryButton>
          </div>
          {err && (
            <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>
          )}
        </form>
      </div>
    </AdminCard>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CustomModelsTab() {
  const [items, setItems] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    id: "", label: "", model_name: "", provider: "openai", kind: "text", tier: "free", api_key: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [models, provs] = await Promise.all([adminListCustomModels(), adminListProviders()]);
      setItems(models);
      setProviders(provs);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminAddCustomModel(form);
      toast.success("Custom model added");
      setForm({ id: "", label: "", model_name: "", provider: "openai", kind: "text", tier: "free", api_key: "" });
      await load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.label}?`)) return;
    try {
      await adminDeleteCustomModel(m.id, m.kind);
      toast.success("Deleted");
      await load();
    } catch (ex) {
      toast.error(formatApiError(ex));
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="custom-models-tab">
      <ProvidersSection providers={providers} onReload={load} />

      {/* Add custom model form */}
      <AdminCard>
        <div className="p-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Add custom model</p>
          <h3 className="text-lg font-semibold text-slate-900 mt-1 mb-1">Bring your own API key</h3>
          <p className="text-sm text-slate-500 mb-5">
            Models added here use the API key you provide instead of the shared key. Keys are encrypted at rest and never exposed to non-admins.
          </p>
          <form onSubmit={add} className="grid md:grid-cols-2 gap-4" data-testid="custom-model-form">
            <AdminFormInput
              label="Internal ID / alias"
              type="text"
              required
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              placeholder="e.g. my-openai-fast"
              hint="Your internal nickname. Unique per kind."
              data-testid="cm-id"
            />
            <AdminFormInput
              label="Display label"
              type="text"
              required
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. My GPT-4o"
              data-testid="cm-label"
            />
            <div className="md:col-span-2">
              <AdminFormInput
                label="Provider model ID *"
                type="text"
                required
                value={form.model_name}
                onChange={(e) => setForm({ ...form, model_name: e.target.value })}
                placeholder={
                  form.provider === "openai"
                    ? (form.kind === "image" ? "gpt-image-1" : "gpt-4o-mini")
                    : form.provider === "anthropic"
                    ? "claude-sonnet-4-5-20250929"
                    : (form.kind === "image" ? "gemini-2.5-flash-image-preview" : "gemini-2.0-flash")
                }
                hint="The exact model ID sent to the provider SDK."
                data-testid="cm-model-name"
              />
            </div>
            <div className="md:col-span-2">
              <AdminFormSelect
                label="Provider"
                value={form.provider}
                onChange={(e) => setForm({ ...form, provider: e.target.value })}
                data-testid="cm-provider"
              >
                {providers.map((p) => {
                  const compatible = p.kind === "both" || p.kind === form.kind;
                  return (
                    <option key={p.name} value={p.name}>
                      {p.label} ({p.name}){p.built_in ? "" : " · custom"}{compatible ? "" : ` — ${p.kind} only`}
                    </option>
                  );
                })}
              </AdminFormSelect>
              {(() => {
                const sel = providers.find((p) => p.name === form.provider);
                if (!sel) return null;
                const compatible = sel.kind === "both" || sel.kind === form.kind;
                return (
                  <div className="mt-2 space-y-1">
                    <p className="text-xs text-slate-400 font-mono break-all">URL: {sel.base_url}</p>
                    {!compatible && (
                      <p className="text-xs text-amber-600">
                        ⚠ This provider only supports <strong>{sel.kind}</strong> models. Switch Kind to <strong>{sel.kind}</strong> below.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
            <AdminFormSelect
              label="Kind"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value })}
              data-testid="cm-kind"
            >
              <option value="text">Text</option>
              <option value="image">Image</option>
            </AdminFormSelect>
            <AdminFormSelect
              label="Tier"
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              data-testid="cm-tier"
            >
              <option value="free">Free</option>
              <option value="premium">Premium</option>
            </AdminFormSelect>
            <div className="md:col-span-2">
              <AdminFormInput
                label="API key"
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="sk-..."
                data-testid="cm-api-key"
              />
            </div>
            {err && (
              <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{err}</div>
            )}
            <div className="md:col-span-2">
              <AdminPrimaryButton
                type="submit"
                busy={busy}
                busyLabel="Adding…"
                icon={<Plus size={14} weight="bold" />}
                className="w-full justify-center"
                data-testid="cm-submit"
              >
                Add model
              </AdminPrimaryButton>
            </div>
          </form>
        </div>
      </AdminCard>

      {/* Custom models list */}
      <AdminCard header={`Your custom models · ${items.length}`} data-testid="custom-models-list">
        {loading ? (
          <p className="p-6 text-slate-400 text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-slate-400 text-sm">No custom models yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  {["Model", "Provider", "Kind", "Tier", "Key", ""].map((h) => (
                    <th key={h} className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id + m.kind} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cm-row-${m.id}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-slate-900">{m.label}</div>
                      <div className="text-xs text-slate-400 font-mono">{m.id}</div>
                      {m.model_name && m.model_name !== m.id && (
                        <div className="text-xs text-slate-500 mt-0.5">→ {m.model_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-500 text-xs">{m.provider}</td>
                    <td className="px-6 py-3 text-slate-500 text-xs">{m.kind}</td>
                    <td className="px-6 py-3">
                      <AdminBadge variant={m.tier === "premium" ? "premium" : "free"}>
                        {m.tier === "premium" ? <Crown size={10} weight="fill" /> : <Lightning size={10} weight="fill" />}
                        {m.tier}
                      </AdminBadge>
                    </td>
                    <td className="px-6 py-3 text-slate-500 text-xs font-mono">{m.key_masked}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={() => remove(m)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        data-testid={`cm-delete-${m.id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </div>
  );
}
